import { defaultTableState } from "@/consts";
import type { NavigateFrom, SearchFrom } from "@/types/common";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import _ from "lodash";
import LZString from "lz-string";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import type { TableSearchParams, TableUrlState } from "../types/data-table";

const tableStateSchema = z.object({
  sorting: z
    .array(z.object({ id: z.string(), desc: z.boolean() }))
    .default(defaultTableState.sorting),
  columnFilters: z
    .array(
      z.object({
        id: z.string(),
        value: z.unknown().refine((v) => v !== null && v !== undefined),
      }),
    )
    .default(defaultTableState.columnFilters),
  globalFilter: z.string().default(defaultTableState.globalFilter),
  pagination: z
    .object({
      cursor: z.string().nullable().default(defaultTableState.pagination.cursor),
      pageSize: z.number().int().positive().default(defaultTableState.pagination.pageSize),
      cursorHistory: z
        .array(z.string().nullable())
        .default(defaultTableState.pagination.cursorHistory),
    })
    .default({
      cursor: null,
      pageSize: defaultTableState.pagination.pageSize,
      cursorHistory: defaultTableState.pagination.cursorHistory,
    }),
  showDeleted: z.boolean().default(false),
});

export const tableSearchSchema = z.object({
  q: z
    .string()
    .nullish()
    .transform((compressed) => {
      if (!compressed) return compressState(defaultTableState);
      const decompressed = safeDecompress(compressed, defaultTableState);
      const result = tableStateSchema.safeParse(decompressed);
      return result.success ? compressed : compressState(defaultTableState);
    }),
});

export function safeDecompress<T extends TableUrlState>(
  compressed: string | null,
  fallback: T = defaultTableState as unknown as T,
): T {
  try {
    if (!compressed) return fallback;
    const decompressed = LZString.decompressFromEncodedURIComponent(compressed);
    if (decompressed) return JSON.parse(decompressed) as T;
  } catch (e) {
    console.error("Failed to decompress state:", e);
  }
  return fallback;
}

export function compressState(data: unknown): string | null {
  try {
    const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(data));
    if (compressed && LZString.decompressFromEncodedURIComponent(compressed)) {
      return compressed;
    }
  } catch (e) {
    console.error("Failed to compress state:", e);
  }
  return null;
}

function parseUrlState(search: TableSearchParams): TableUrlState {
  if (!search.q) return defaultTableState;
  const parsed = safeDecompress<any>(search.q, defaultTableState);
  return {
    sorting: parsed.sorting ?? defaultTableState.sorting,
    columnFilters: parsed.columnFilters ?? defaultTableState.columnFilters,
    globalFilter: parsed.globalFilter ?? defaultTableState.globalFilter,
    pagination: {
      cursor: parsed.pagination?.cursor ?? defaultTableState.pagination.cursor,
      pageSize: parsed.pagination?.pageSize ?? defaultTableState.pagination.pageSize,
      cursorHistory: parsed.pagination?.cursorHistory ?? defaultTableState.pagination.cursorHistory,
    },
    showDeleted: parsed.showDeleted ?? defaultTableState.showDeleted,
  };
}

function buildSearchParams(state: TableUrlState): TableSearchParams {
  if (_.isEqual(state, defaultTableState)) return {};
  const compressed = compressState(state);
  return compressed ? { q: compressed } : {};
}

interface UseTableUrlStateOptions {
  navigateFrom: NavigateFrom;
  searchFrom: SearchFrom;
}

export default function useTableUrlState({ navigateFrom, searchFrom }: UseTableUrlStateOptions) {
  const navigate = useNavigate({ from: navigateFrom });
  const search = useSearch({ from: searchFrom }) as TableSearchParams;

  const state = useMemo(() => parseUrlState(search), [search]);

  const update = useCallback(
    (next: TableUrlState) => {
      navigate({ search: buildSearchParams(next), replace: true });
    },
    [navigate],
  );

  const setGlobalFilter = useCallback(
    (value: string | ((prev: string) => string)) => {
      const resolved = typeof value === "function" ? value(state.globalFilter) : value;
      update({
        ...state,
        globalFilter: resolved,
        pagination: { ...state.pagination, cursor: null },
      });
    },
    [state, update],
  );

  const setColumnFilters = useCallback(
    (value: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)) => {
      const resolved = typeof value === "function" ? value(state.columnFilters) : value;
      update({
        ...state,
        columnFilters: resolved,
        pagination: { ...state.pagination, cursor: null },
      });
    },
    [state, update],
  );

  const setSorting = useCallback(
    (value: SortingState | ((prev: SortingState) => SortingState)) => {
      const resolved = typeof value === "function" ? value(state.sorting) : value;
      update({
        ...state,
        sorting: resolved,
        pagination: { ...state.pagination, cursor: null },
      });
    },
    [state, update],
  );

  const setPagination = useCallback(
    (
      value:
        | TableUrlState["pagination"]
        | ((prev: TableUrlState["pagination"]) => TableUrlState["pagination"]),
    ) => {
      const resolved = typeof value === "function" ? value(state.pagination) : value;
      const cursor = resolved.pageSize !== state.pagination.pageSize ? null : resolved.cursor;

      update({
        ...state,
        pagination: {
          pageSize: resolved.pageSize,
          cursor,
          cursorHistory: resolved.cursorHistory,
        },
      });
    },
    [state, update],
  );

  const setShowDeleted = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const resolved = typeof value === "function" ? value(state.showDeleted || false) : value;
      update({
        ...state,
        showDeleted: resolved,
        pagination: { ...state.pagination, cursor: null },
      });
    },
    [state, update],
  );

  return {
    sorting: state.sorting,
    showDeleted: state.showDeleted,
    columnFilters: state.columnFilters,
    globalFilter: state.globalFilter,
    pagination: state.pagination,
    setGlobalFilter,
    setColumnFilters,
    setSorting,
    setPagination,
    setShowDeleted,
  };
}
